import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { RadioButton } from 'src/components/radioButton/RadioButton';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { absoluteURL } from 'src/lib/absoluteURL';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { variantRouteConfig } from '../../routes';

interface Props {
  // Ideally this would be a fragment.
  data: GraphQLTypes.VariantGeneralSettingsPageQuery;
  isPublic: boolean;
}

export const PublicVariantSettingsCard = ({ data, isPublic }: Props) => {
  const { graphId, graphVariant } = useRouteParams(variantRouteConfig);
  const history = useHistory();
  const location = useLocation();
  const graph = data?.graph;

  const publicLink = absoluteURL(
    variantRouteConfig.path({
      graphId,
      graphVariant,
      graphVisibilityType: 'public',
    }),
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Public: {isPublic ? 'ON' : 'OFF'}</CardTitle>
          <CardDescription>
            {isPublic && publicLink ? (
              <>
                Your graph is publicly accessible at{' '}
                <Link
                  to={publicLink}
                  isExternal
                  className="whitespace-break-spaces"
                >
                  {publicLink}
                </Link>
                . Anyone on the internet with this link to your graph will be
                able to see its README, browse its schema, and query it with
                GraphOS Studio Explorer.
              </>
            ) : (
              <>
                Turn this on to allow anyone on the public internet to visit
                your variant's README, browse its schema, and query it with
                GraphOS Studio Explorer.
              </>
            )}
          </CardDescription>
          <PermissionWarning
            graphId={graphId}
            graphPermissions="canManageVariants"
            childRenderer={(canManageVariants) => (
              <Tooltip
                label="You are not allowed to change this setting for this variant. Please ask a graph admin to update it, or contact support if you need help."
                disabled={canManageVariants}
              >
                <Button
                  variant="destructive"
                  isDisabled={!canManageVariants}
                  onClick={() => {
                    history.push(
                      setOverlayVisibleLocation({
                        location,
                        overlayName: Config.modals.updatePublicVariant,
                      }),
                    );
                  }}
                >
                  Change
                </Button>
              </Tooltip>
            )}
          />
        </CardHeader>
      </Card>

      {graph && (
        <URLModal name={Config.modals.updatePublicVariant}>
          {({ isOpen, closeModal }) => (
            <ChangePublicVariantStatusModal
              isOpen={isOpen}
              isPublic={isPublic}
              graphVariant={graphVariant || ''}
              graphId={graph.id}
              closeModal={closeModal}
            />
          )}
        </URLModal>
      )}
    </>
  );
};

function ChangePublicVariantStatusModal({
  isOpen,
  graphId,
  graphVariant,
  isPublic,
  closeModal,
}: {
  isOpen: boolean;
  graphId: string;
  graphVariant: string;

  isPublic: boolean;
  closeModal: () => void;
}) {
  const [toggleSetting, setToggleSetting] = useState<boolean>(isPublic);
  const [updateIsPublic, { loading, error }] = useMutation<
    GraphQLTypes.UpdateIsPublic,
    GraphQLTypes.UpdateIsPublicVariables
  >(
    gql`
      mutation UpdateIsPublic(
        $graphId: ID!
        $graphVariant: String!
        $isPublic: Boolean!
      ) {
        service(id: $graphId) {
          variant(name: $graphVariant) {
            id
            updateVariantIsPublic(isPublic: $isPublic) {
              id
              isPublic
            }
          }
        }
      }
    `,
    {
      variables: {
        graphId,
        graphVariant,
        isPublic: toggleSetting,
      },
      onCompleted: () => closeModal(),
    },
  );

  return (
    <PermissionWarning
      graphId={graphId}
      graphPermissions={['canManageVariants']}
      childRenderer={(canManageVariants) => (
        <>
          <Modal isOpen={isOpen} size="lg" onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Toggle Public Settings</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <RadioButton
                  title={`On${isPublic ? ' (current)' : ''}`}
                  description="Your variant's README, Schema, and Explorer will be accessible to anyone on the internet with its link."
                  disabled={loading || !canManageVariants}
                  selected={toggleSetting}
                  onClick={() => {
                    setToggleSetting(true);
                  }}
                />
                <RadioButton
                  title={`Off${isPublic ? '' : ' (current)'}`}
                  description="Your variant will only be accessible to members of your organization."
                  disabled={!canManageVariants}
                  selected={!toggleSetting}
                  onClick={() => {
                    setToggleSetting(false);
                  }}
                />
                {error && <ErrorMessage />}
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button variant="secondary" onClick={() => closeModal()}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    isDisabled={loading || isPublic === toggleSetting}
                    isLoading={loading}
                    loadingText="Save"
                    onClick={() => updateIsPublic()}
                  >
                    Save
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}
    />
  );
}
